import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Calendar,
    DollarSign,
    Activity,
    Mail,
    TrendingUp,
    ArrowRight,
} from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency, formatDateTime } from "../../lib/format";

const Stat = ({ icon: Icon, label, value, trend, tone = "saffron" }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between">
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone === "saffron" ? "bg-saffron-50 text-saffron-600" : tone === "teal" ? "bg-teal-50 text-teal-600" : "bg-navy-50 text-navy-600"}`}>
                <Icon className="w-5 h-5" />
            </span>
            {trend && <span className="text-xs font-semibold text-teal-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {trend}</span>}
        </div>
        <div className="mt-3 text-xs uppercase tracking-wider text-ink-light font-semibold">{label}</div>
        <div className="font-display text-3xl font-semibold text-ink mt-0.5">{value}</div>
    </div>
);

const Overview = () => {
    const { bookings, inquiries } = useData();

    const stats = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const month = new Date().toISOString().slice(0, 7);
        const todayCount = bookings.filter((b) => b.createdAt?.slice(0, 10) === today).length;
        const monthBookings = bookings.filter((b) => b.createdAt?.slice(0, 7) === month);
        const monthRevenue = monthBookings.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.totalAmount || 0), 0);
        const active = bookings.filter((b) => b.status === "confirmed").length;
        const newInquiries = inquiries.filter((i) => i.status === "new").length;
        return { todayCount, monthBookings: monthBookings.length, monthRevenue, active, newInquiries };
    }, [bookings, inquiries]);

    const recent = bookings.slice(0, 6);

    return (
        <div data-testid="admin-overview">
            <h1 className="font-display text-3xl text-ink">Operations overview</h1>
            <p className="text-ink-light text-sm mt-1">Live snapshot of your SouthTrip business.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <Stat icon={Calendar} label="Bookings today" value={stats.todayCount} tone="saffron" />
                <Stat icon={Calendar} label="Bookings this month" value={stats.monthBookings} tone="teal" />
                <Stat icon={DollarSign} label="Revenue (this month)" value={formatCurrency(stats.monthRevenue)} tone="navy" />
                <Stat icon={Activity} label="Active bookings" value={stats.active} tone="saffron" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <h2 className="font-semibold text-ink">Recent bookings</h2>
                        <Link to="/admin/bookings" className="text-xs text-saffron-600 font-semibold flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {recent.length === 0 && <div className="p-8 text-center text-ink-light text-sm">No bookings yet.</div>}
                        {recent.map((b) => (
                            <div key={b.id} className="flex items-center gap-4 px-5 py-3" data-testid={`recent-${b.id}`}>
                                <span className="w-9 h-9 rounded-lg bg-saffron-50 flex items-center justify-center text-saffron-600 text-xs font-semibold">
                                    {b.type === "cab" ? "CAB" : "PKG"}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-ink text-sm truncate">{b.cabName || b.packageName}</div>
                                    <div className="text-xs text-ink-light">{b.customer.name} · {formatDateTime(b.createdAt)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-semibold text-saffron-600 text-sm">{formatCurrency(b.totalAmount)}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-ink-light">{b.status}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <h2 className="font-semibold text-ink flex items-center gap-2"><Mail className="w-4 h-4 text-teal-600" /> New inquiries</h2>
                        <Link to="/admin/inquiries" className="text-xs text-saffron-600 font-semibold">View</Link>
                    </div>
                    <div className="p-5 space-y-3">
                        {inquiries.slice(0, 4).map((i) => (
                            <div key={i.id} className="text-sm">
                                <div className="font-semibold text-ink">{i.name}</div>
                                <div className="text-xs text-ink-light line-clamp-1">{i.subject || i.message}</div>
                            </div>
                        ))}
                        {inquiries.length === 0 && <div className="text-sm text-ink-light">No inquiries yet.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Overview;
